import { Box, Text } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { ISidebarItem } from "../../interface/interface";

interface SidebarItemVisualProps {
  title: string;
  icon: JSX.Element;
  isActive?: boolean;
  /** Anzahl ungelesener Eintraege - sitzt am Symbol, nicht an der Beschriftung (KAN-251, A1).
   * Die Seitenleiste ist im Alltag schmal und zeigt die Beschriftung nur beim Hover; ein
   * Abzeichen neben dem Wort waere dann fast nie zu sehen. */
  badgeCount?: number;
}

/**
 * Die gemeinsame Darstellung eines Eintrags - Symbol, Beschriftung, Hover-/Aktiv-Zustand.
 * `SidebarItem` (Navigation) und `SidebarButtonItem` (Aktion statt Navigation, KAN-251)
 * teilen sie sich, statt sie zu kopieren.
 */
const SidebarItemVisual = ({ title, icon, isActive, badgeCount }: SidebarItemVisualProps) => (
  <Box
    _hover={{ background: "rgba(249,250,252, 0.2)" }}
    background={isActive ? "rgba(249,250,252, 0.2)" : ""}
    fill={isActive ? "yellow.100" : "white"}
    color={isActive ? "yellow.100" : "white"}
    padding="0.5em .875em"
    borderRadius="8px"
  >
    <Box display="flex" gap="1em" className="group items-center">
      <Box height="1.75em" width="1.75em" position="relative">
        {icon}
        {!!badgeCount && (
          <Box
            data-testid="neuigkeiten-badge"
            position="absolute"
            top="-6px"
            right="-8px"
            minWidth="18px"
            height="18px"
            borderRadius="9999px"
            background="#0077a3"
            color="white"
            fontSize="11px"
            lineHeight="18px"
            textAlign="center"
            fontWeight="bold"
            paddingX="4px"
          >
            {badgeCount > 9 ? "9+" : badgeCount}
          </Box>
        )}
      </Box>
      <Box width="100%" className="hidden opacity-0 group-hover:opacity-100 group-hover:flex transition-all">
        <Text className="whitespace-nowrap">{title}</Text>
      </Box>
    </Box>
  </Box>
);

const SidebarItem = ({ title, icon, link, isActive, end }: ISidebarItem) => {
  return (
    <Link to={link}>
      <SidebarItemVisual title={title} icon={icon} isActive={isActive} />
    </Link>
  );
};

/**
 * Ein Eintrag, der einen Bereich oeffnet statt zu navigieren (KAN-251). `SidebarItem`
 * ist ein `<Link>` - der Neuigkeiten-Eintrag braucht stattdessen einen Klick-Handler,
 * sieht aber genauso aus (gleiche Abstaende, gleicher Hover-/Aktiv-Zustand).
 */
export const SidebarButtonItem = ({
  title,
  icon,
  isActive,
  badgeCount,
  onClick,
  markierung,
}: SidebarItemVisualProps & { onClick: () => void; markierung?: string }) => (
  // Die Markierung erlaubt es einem geoeffneten Bereich, den Klick auf genau diesen Knopf
  // nicht als "daneben geklickt" zu werten - sonst schloesse er zu und der Knopf oeffnete
  // ihn im selben Zug wieder.
  <button type="button" onClick={onClick} className="w-full text-left" data-schalter={markierung}>
    <SidebarItemVisual title={title} icon={icon} isActive={isActive} badgeCount={badgeCount} />
  </button>
);

export default SidebarItem;
